import { get as getStore } from 'svelte/store'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import merge from 'lodash/merge'
import { v4 } from 'uuid'
import { loadComponents } from '@/api/component'
import { create as createFetch } from '@/api/fetch'
import { copyObject } from '@/api/helpers'
import { createBlankRender, fetchRender, page, isAccessible, load, preload, selectEntry } from '@/api/render'
import { apis, commonName } from '@/api/meta'
import { create as createRouter } from '@/api/router'
import { create as createStore } from '@/api/store'
import { loadHandlers } from '@/api/handlers'
import factory from '@/api/factory'
import modal from '@/api/browser/modal'
import {
  plugin, nodes, childBlockId, renderProps, nodeProps, nodeFields, pluginProps, nodeIs,
  ROOT, LAYOUT, ROW_BRANCH, ROW,
  COL, COL_UNIQUE, COL_SHARED, PLUGIN, findNode, findBlock,
} from '@/api/template'

export const tokenCookieName = '__Host-token'

export async function create (storeData, url, request) {
  let internalPolicy = null
  let languages = []

  if (IS_BROWSER) {
    languages = [...navigator.languages]

    if (typeof window.trustedTypes !== 'undefined') {
      // This policy will run as a fallback when another policy isn't used
      // If a callback isn't defined to handle a sink in here then an error will be thrown instead
      //
      // Internally Svelte assigns to innerHTML and doesn't provide an easy way to hook into that compilation to add
      // trusted types to it where required, so we can use this default policy to allow assignments that we think
      // are safe to the TrustedHTML sink, for example by using document.createRange().createContextualFragment(source)
      // to inspect the elements that would become part of the page
      //
      // Currently, because we expect that the only sinks will be inside trusted source/URLs that require a nonce, we
      // just allow everything
      window.trustedTypes.createPolicy('default', {
        createHTML: source => source,
        createScript: source => source,
        createScriptURL: url => url,
      })

      // This is an unsafe internal policy that the system can use to let a browser know that input to a sink is allowed
      internalPolicy = window.trustedTypes.createPolicy('internal', {
        createHTML: source => source,
        createScript: source => source,
        createScriptURL: url => url,
      })
    }
  }

  const stores = {}

  storeData = {
    render: createBlankRender(),
    components: [],
    auth: {},
    isOnline: true,
    offlineActions: [],
    icons: {},
    appInitialised: false,
    ...storeData,
  }

  const store = createStore(storeData)
  const router = createRouter(url, storeData.config.components.missing)
  const fetch = await createFetch(request)
  let loadingHandle = null

  if (IS_BROWSER) {
    window.addEventListener('load', () => {
      store.update(store => {
        store.isOnline = navigator.onLine

        return store
      })
    })

    window.addEventListener('online', () => {
      const sync = async () => {
        store.update(store => {
          store.isOnline = true

          return store
        })

        const count = getStore(store).offlineActions.length

        if (count) {
          let successCount = 0

          console.log(`Syncing ${count} offline actions...`)

          try {
            const { offlineActions } = platform.storeData

            while (offlineActions.length) {
              await offlineActions[0]()

              offlineActions.shift()

              store.update(store => {
                store.offlineActions = offlineActions

                return store
              })

              successCount++
            }

            console.log('All offline actions synced')
          } catch (err) {
            console.error(err)
            console.log(`Synced ${successCount} out of ${count} actions`)
            console.log(`The remaining ${count - successCount} actions will be synced when back online`)

            setTimeout(sync, 1000)
          }
        }
      }

      sync()
    })

    window.addEventListener('offline', () => {
      store.update(store => {
        store.isOnline = false

        return store
      })
    })
  }

  const sockets = {}

  const platform = {
    __eventHandlers: {},
    get tokenCookieName () {
      return tokenCookieName
    },
    fetch,
    websocket (url) {
      let socket = sockets[url]

      // If a socket doesn't exist yet, or its state is closing/closed
      // then we need to create an cache a new socket
      if (!socket || socket.readyState === 2 || socket.readyState === 3) {
        socket = new WebSocket(url)

        sockets[url] = socket
      }

      return socket
    },
    store,
    get storeData () {
      return getStore(platform.store)
    },
    router,
    languages,
    dateFormatter: new Intl.DateTimeFormat(languages),
    numberFormatter: new Intl.NumberFormat(languages, { minimumFractionDigits: 2 }),
    components: {},
    listeners: {},
    query: {
      inParams (arr) {
        if (!Array.isArray(arr)) {
          throw new Error('inParams must be given an array')
        }

        return '?'.repeat(arr.length).split('').join(', ')
      },
    },
    animations: {
      fade: {
        endingClassNames: ['opacity-1'],
        startingClassNames: ['opacity-0'],
      },
      slideUp: {
        endingClassNames: ['opacity-1', 'translate-y-0'],
        startingClassNames: ['opacity-0', 'translate-y-20'],
      },
      slideLeft: {
        endingClassNames: ['opacity-1', 'translate-x-0'],
        startingClassNames: ['opacity-0', 'translate-x-20'],
      },
      slideRight: {
        endingClassNames: ['opacity-1', 'translate-y-0'],
        startingClassNames: ['opacity-0', '-translate-x-20'],
      },
      slideDown: {
        endingClassNames: ['opacity-1', 'translate-y-0'],
        startingClassNames: ['opacity-0', '-translate-y-20'],
      },
      scale: {
        endingClassNames: ['opacity-1', 'scale-100'],
        startingClassNames: ['opacity-0', 'scale-0'],
      },
    },
    api: {
      server: {
        head: [],
        footer: [],
      },
      browser: {
        modal,
      },
      core: {
        uuid: v4,
        copyObject,
        debounce,
        isEqual,
        merge,
        createBlankRender,
        cacheResources: factory.cacheResources,
        listen (event, handler) {
          if (!event) {
            throw new Error('no event supplied')
          }

          if (typeof handler !== 'function') {
            throw new Error('listen handler must be a function')
          }

          platform.listeners[event] = platform.listeners[event] || {}
          platform.listeners[event].system = platform.listeners[event].system || []

          if (!platform.listeners[event].system.find(h => h === handler)) {
            platform.listeners[event].system.push(handler)
          }
        },
        async dispatch (event, data) {
          if (platform.listeners[event]) {
            for (const key in platform.listeners[event]) {
              const handler = platform.listeners[event][key]

              if (Array.isArray(handler)) {
                for (const h of handler) {
                  await h({ platform, data })
                }
              } else {
                await handler({ platform, data })
              }
            }
          }
        },
        store (name) {
          if (!stores[name]) {
            stores[name] = createStore()
          }

          return stores[name]
        },
        date: {
          format (date, options = {}) {
            if (typeof date === 'string') {
              date = new Date(date)
            }

            return new Intl.DateTimeFormat(platform.languages, options).format(date)
          },
        },
        number: {
          format (number, options = {}) {
            return new Intl.NumberFormat(languages, options).format(number)
          },
        },
        loading: {
          show (message = '') {
            if (typeof window === 'undefined') {
              return
            }

            if (platform.storeData.loading.show) {
              return
            }

            clearTimeout(loadingHandle)
            loadingHandle = setTimeout(() => {
              platform.store.update(store => {
                store.loading.show = true
                store.loading.message = message

                return store
              })
            }, 200)
          },
          hide () {
            clearTimeout(loadingHandle)
            loadingHandle = setTimeout(() => {
              platform.store.update(store => {
                store.loading.show = false
                store.loading.message = ''

                return store
              })
            }, 250)
          },
          setMessage (message) {
            platform.store.update(store => {
              store.loading.message = message
              return store
            })
          },
        },
        captured (el, fallback) {
          if (!el || typeof window === 'undefined' || !window.cms._captured) {
            return fallback
          }

          const input = window.cms._captured.find(c => c.target === el)

          if (input) {
            return input.value
          }

          return fallback
        },
        isLoggedIn () {
          return claimsAreValid(platform.storeData.auth.claims)
        },
        scopes () {
          return scopes(platform)
        },
        hasScope (...scopes) {
          return hasScope(platform, false, ...scopes)
        },
        hasScopeStrict (...scopes) {
          return hasScope(platform, true, ...scopes)
        },
        meta: {
          async apis (internalToken) {
            return apis(platform, internalToken)
          },
          async commonName () {
            return commonName(platform)
          },
        },
        isLCP (path) {
          path = (path || '').split('?').shift()

          return platform.storeData.lcpResources.includes(path)
        },
        render: {
          // Default is false for backwards compatibility with old templates
          // New templates should set this to true before rendering
          isAboveTheFold: false,

          async fetch (pathname, blockId) {
            return fetchRender(platform, pathname, blockId)
          },
          async page (pathname, blockId) {
            return page(platform, pathname, blockId)
          },
          async isAccessible (pathname) {
            return isAccessible(platform, pathname)
          },
          async preload (pathname) {
            return preload(platform, pathname)
          },
          async load (pathname) {
            return load(platform, pathname)
          },
        },
        setTitle (newTitle) {
          if (IS_BROWSER) {
            document.title = newTitle
          }

          platform.store.update(store => {
            store.render.page.title = newTitle

            return store
          })
        },
        setDescription (newDescription) {
          platform.store.update(store => {
            store.render.page.description = newDescription

            return store
          })
        },
        setCanonical (newCanonical) {
          if (IS_BROWSER) {
            document.querySelector("link[rel='canonical']")?.setAttribute('href', newCanonical)
          }

          platform.store.update(store => {
            store.render.page.canonicalURL = newCanonical

            return store
          })
        },
        setThumbnail (newThumbnail) {
          platform.store.update(store => {
            store.render.page.thumbnail = newThumbnail

            return store
          })
        },
        async selectEntry (routes) {
          return selectEntry(platform, routes)
        },
        async loadComponents (refs = [], options = {}) {
          return loadComponents(platform, refs, options)
        },
        async loadHandlers () {
          return loadHandlers(platform)
        },
        async initPlugins (plugins = {}) {
          plugins = platform.api.core.copyObject(plugins)

          await Promise.all(Object.entries(plugins).map(entry => new Promise(resolve => {
            const plugin = entry.pop()

            if (!platform.components[plugin.ref]) {
              return resolve()
            }

            const { preload, classNames, isMissing } = platform.components[plugin.ref]

            if (isMissing) {
              delete plugin.preloadData
              delete plugin.classNames
              delete plugin.fields

              plugin.missingRef = plugin.ref
            }

            if (Array.isArray(classNames)) {
              plugin.classNames = classNames
            } else {
              plugin.classNames = []
            }

            if (!IS_BROWSER && typeof preload === 'function') {
              const data = {
                platform,
                pluginId: plugin.id,
                fields: platform.api.core.copyObject(plugin.fields),
              }

              preload(data).then(data => {
                plugin.preloadData = data

                resolve()
              })
            } else {
              resolve()
            }
          })))

          return plugins
        },
        on (event, func) {
          if (!IS_BROWSER) {
            return
          }

          const valid = ['init']

          if (!valid.includes(event)) {
            throw new Error(`Event type must be one of: ["${valid.join('", "')}"]`)
          }

          if (typeof func !== 'function') {
            throw new Error('Event handler must be a function')
          }

          platform.__eventHandlers[event] = platform.__eventHandlers[event] || []

          platform.__eventHandlers[event].push(func)
        },
        template: {
          ROOT,
          LAYOUT,
          ROW_BRANCH,
          ROW,
          COL,
          COL_UNIQUE,
          COL_SHARED,
          PLUGIN,
          plugin (render, node) {
            return plugin(platform, render, node)
          },
          nodes,
          renderProps,
          nodeProps,
          nodeFields,
          pluginProps,
          nodeIs,
          childBlockId,
          findNode,
          findBlock,
        },
        replaceTokens (str) {
          const tokens = [...str.matchAll(/{[^}]+}/g)].flat()

          for (const token of tokens) {
            const trimmed = token.slice(1, -1)
            const parts = trimmed.split(':')
            const command = (parts.shift() || '').trim().toLowerCase()

            if (parts.length === 0) {
              const d = new Date()

              if (command === 'query') {
                str = str.replaceAll(token, () => router.query)
              }

              if (command === 'year') {
                str = str.replaceAll(token, d.getFullYear())
              }

              if (command === 'month') {
                str = str.replaceAll(token, d.getMonth() + 1)
              }

              if (command === 'day') {
                str = str.replaceAll(token, d.getDate())
              }

              if (command === 'hour') {
                str = str.replaceAll(token, d.getHours())
              }

              if (command === 'minute') {
                str = str.replaceAll(token, d.getMinutes())
              }

              if (command === 'second') {
                str = str.replaceAll(token, d.getSeconds())
              }
            }

            if (parts.length > 0) {
              if (command === 'q') {
                const key = (parts.shift() || '').trim()
                const value = router.params.get(key)
                const fallback = parts.join(':') || ''
                const text = value || fallback
                const escaped = text
                  .replaceAll(`&`, '&amp;')
                  .replaceAll(`<`, '&lt;')
                  .replaceAll(`>`, '&gt;')
                  .replaceAll(`"`, '&quot;')
                  .replaceAll(`'`, '&#x27;')

                str = str.replaceAll(token, escaped)
              }
            }
          }

          return str
        },
        unsafe: {
          trustedHTML (source) {
            return internalPolicy ? internalPolicy.createHTML(source) : source
          },
          trustedScript (source) {
            return internalPolicy ? internalPolicy.createScript(source) : source
          },
          trustedScriptURL (url) {
            return internalPolicy ? internalPolicy.createScriptURL(url) : url
          },
        },
      },
    },
  }

  return platform
}

function claimsAreValid (claims) {
  if (!claims || !claims.sub || !claims.exp) {
    return false
  }

  const currentTime = Math.floor(Date.now() / 1000)

  if (claims.exp <= currentTime) {
    return false
  }

  return true
}

function scopes (platform) {
  const { claims } = platform.storeData.auth

  if (!claimsAreValid(claims)) {
    return []
  }

  if (!Array.isArray(claims.scopes)) {
    return []
  }

  return claims.scopes
}

function hasScope (platform, strict, ...checkScopes) {
  const userScopes = scopes(platform)

  if (!strict && userScopes.includes('super')) {
    return true
  }

  for (const scope of checkScopes) {
    if (userScopes.includes(scope)) {
      return true
    }
  }

  return false
}
